import { Fragment, type ReactNode } from "react";
import { appointmentStatusLabel } from "@/constants/appointments/appointments-constants";
import { isoDate, isoTime } from "@/lib/utils";
import { cn } from "cn";
import type { ClientAppointmentVisitLine } from "@/mappers/clients/client-appointment-display";
import type { AppointmentStatus } from "@/types/appointments/api-types/appointment-types";
import { useIntl } from "react-intl";

interface ClientAppointmentVisitBlockProps {
  appointmentDate: string;
  startTime: string | null;
  status: AppointmentStatus;
  lines: ClientAppointmentVisitLine[];
  children?: ReactNode;
}

// A visit block doesn't label completed visits - the history list is all completed visits.
const statusLabel = (status: AppointmentStatus): string | null =>
  status === "completed" ? null : appointmentStatusLabel(status);

export function ClientAppointmentVisitBlock({
  appointmentDate,
  startTime,
  status,
  lines,
  children,
}: ClientAppointmentVisitBlockProps) {
  const intl = useIntl();
  const timeLabel = startTime ? intl.formatTime(isoTime(startTime), { format: "clock" }) : null;
  const statusText = statusLabel(status);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-6 gap-y-1">
        <span className="font-semibold">
          {intl.formatDate(isoDate(appointmentDate), { format: "long" })}
          {statusText ? (
            <>
              <span
                className={cn(
                  "px-1.5 font-normal",
                  status === "cancelled"
                    ? "text-destructive"
                    : "text-muted-foreground"
                )}
              >
                ·
              </span>
              <span
                className={cn(
                  "font-medium",
                  status === "cancelled"
                    ? "text-destructive"
                    : "text-muted-foreground"
                )}
              >
                {statusText}
              </span>
            </>
          ) : null}
        </span>
        <span className="col-start-3 text-right font-semibold">{timeLabel}</span>
        {lines.map((line) => {
          const staff = line.staff_name?.trim();
          return (
            <Fragment key={line.key}>
              <span className="truncate text-sm">{line.name.trim()}</span>
              <span className="whitespace-nowrap text-right text-sm">
                {staff ? `with ${staff}` : ""}
              </span>
              <span className="min-w-18 text-right text-sm tabular-nums">
                {intl.formatNumber(line.price, { format: "money" })}
              </span>
            </Fragment>
          );
        })}
      </div>
      {children}
    </div>
  );
}
